import PropTypes from 'prop-types';
import React from 'react';

/**
 * John (CEO), 2026-10-02: the results of a question as replies and the share
 * of each answer, e.g. "20 replies: 40% Very poor, 10% Poor, 10% Okay, 20%
 * Good, 20% Very good", and Yes / No for a yes-no question.
 *
 * Rating answers run red to green so the shape of the result reads at a
 * glance; other answers share one neutral colour, since "60-69" is not better
 * than "50-59". The percentage and count are always written out.
 */

const RATING_COLOUR = {
  1: 'bg-rose-500',
  2: 'bg-orange-400',
  3: 'bg-amber-300',
  4: 'bg-lime-500',
  5: 'bg-emerald-600'
};

const colourOf = (question, answer) => {
  if (question.type === 'rating5') return RATING_COLOUR[answer.value] || 'bg-slate-400';
  if (question.type === 'yesno') return answer.value ? 'bg-indigo-500' : 'bg-slate-400';
  return 'bg-indigo-400';
};

export const percentOf = share => `${Math.round(share * 100)}%`;

const AnswerBreakdown = ({ question, compact, testId }) => {
  const { replies, answers } = question;
  // Single-answer questions get one stacked bar; multiple choice cannot,
  // because one guest can be in several bars.
  const stacked = question.type !== 'multi';
  return (
    <div className="flex flex-col gap-2 min-w-0" data-testid={testId}>
      {!compact && (
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-bold text-slate-900 min-w-0">{question.label}</span>
          <span className="text-xs text-slate-500 whitespace-nowrap tabular-nums">
            {replies} {replies === 1 ? 'reply' : 'replies'}
            {question.average !== null && question.average !== undefined
              ? ` · ${question.average.toFixed(1)} avg`
              : ''}
          </span>
        </div>
      )}
      {replies === 0 ? (
        <p className="text-xs text-slate-500 m-0">No replies yet.</p>
      ) : (
        <>
          {stacked && (
            <div
              className="flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100"
              aria-hidden="true">
              {answers
                .filter(a => a.count > 0)
                .map(a => (
                  <span
                    key={String(a.value)}
                    className={`h-full ${colourOf(question, a)}`}
                    style={{ width: `${a.share * 100}%` }}
                  />
                ))}
            </div>
          )}
          <ul
            className={`list-none m-0 p-0 grid gap-x-4 gap-y-1 ${
              compact ? 'grid-cols-5' : 'grid-cols-1'
            }`}>
            {answers.map(a => (
              <li
                key={String(a.value)}
                className={compact ? 'min-w-0' : 'flex items-center gap-3'}
                data-testid={testId ? `${testId}-${String(a.value)}` : undefined}>
                {compact ? (
                  <>
                    <span className="block text-xs font-bold text-slate-800 tabular-nums">
                      {percentOf(a.share)}
                    </span>
                    {/* Five columns are too narrow for "Very poor": stars here, the
                        words on hover and in the full breakdown. */}
                    <span className="block text-xs text-slate-500 truncate" title={a.label}>
                      {question.type === 'rating5' ? `${a.value}★` : a.label}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="flex items-start gap-2 w-48 flex-none min-w-0">
                      <span
                        className={`w-2 h-2 mt-1 rounded-full flex-none ${colourOf(question, a)}`}
                        aria-hidden="true"
                      />
                      {/* Wraps rather than truncates: two answers can share a long
                          start ("Recommendation of a friend…", "…of an agent"). */}
                      <span className="text-xs text-slate-700 leading-snug break-words min-w-0">
                        {a.label}
                      </span>
                    </span>
                    {!stacked && (
                      <span
                        className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"
                        aria-hidden="true">
                        <span
                          className="block h-full rounded-full bg-indigo-400"
                          style={{ width: `${Math.min(1, a.share) * 100}%` }}
                        />
                      </span>
                    )}
                    <span className="ml-auto text-xs font-bold text-slate-800 tabular-nums w-10 text-right">
                      {percentOf(a.share)}
                    </span>
                    <span className="text-xs text-slate-500 tabular-nums w-8 text-right">
                      {a.count}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          {question.type === 'multi' && !compact && (
            <p className="text-xs text-slate-500 m-0">
              Guests could pick more than one, so these add up to more than 100%.
            </p>
          )}
        </>
      )}
    </div>
  );
};

AnswerBreakdown.propTypes = {
  question: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.string,
    replies: PropTypes.number,
    average: PropTypes.number,
    answers: PropTypes.array
  }).isRequired,
  compact: PropTypes.bool,
  testId: PropTypes.string
};

export default AnswerBreakdown;
